import { makeAutoObservable, runInAction } from 'mobx';
import { calendarAuthApi } from '../api/calendar_auth_api';
import type { CalendarListItem } from '../types/calendar';

export class CalendarAuthStore {
  availableCalendars: CalendarListItem[] = [];
  syncedCalendars: string[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get OAuth authorization URL for Google Calendar
   */
  async fetchAuthUrl(identity: string): Promise<string | null> {
    try {
      return await calendarAuthApi.getAuthUrl(identity);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to get auth URL';
      });
      return null;
    }
  }

  /**
   * Fetch available Google calendars for user
   */
  async fetchAvailableCalendars(identity: string): Promise<void> {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const response = await calendarAuthApi.getAvailableCalendars(identity);
      runInAction(() => {
        this.availableCalendars = response.calendars;
        this.syncedCalendars = response.synced_calendars || [];
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Failed to fetch calendars';
      });
    }
  }

  /**
   * Add calendar to synced list
   */
  async addSyncedCalendar(identity: string, calendarId: string): Promise<boolean> {
    try {
      await calendarAuthApi.addSyncedCalendar(identity, calendarId);
      // Refresh available calendars to update sync status
      await this.fetchAvailableCalendars(identity);
      return true;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to add calendar';
      });
      return false;
    }
  }

  /**
   * Remove calendar from synced list
   */
  async removeSyncedCalendar(identity: string, calendarId: string): Promise<boolean> {
    try {
      await calendarAuthApi.removeSyncedCalendar(identity, calendarId);
      // Refresh available calendars to update sync status
      await this.fetchAvailableCalendars(identity);
      return true;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to remove calendar';
      });
      return false;
    }
  }

  /**
   * Save calendar preferences
   */
  async setCalendarPreferences(preferences: string): Promise<string | null> {
    try {
      return await calendarAuthApi.setCalendarPreferences(preferences);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to save preferences';
      });
      return null;
    }
  }

  /**
   * Get calendar preferences
   */
  async fetchCalendarPreferences(): Promise<string> {
    try {
      return await calendarAuthApi.getCalendarPreferences();
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to fetch preferences';
      });
      return '';
    }
  }

  /**
   * Disconnect Google Calendar (remove credentials)
   */
  async disconnectCalendar(identity: string): Promise<boolean> {
    try {
      await calendarAuthApi.disconnectCalendar(identity);
      this.reset();
      return true;
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to disconnect calendar';
      });
      return false;
    }
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.availableCalendars = [];
    this.syncedCalendars = [];
    this.loading = false;
    this.error = null;
  }
}

export const calendarAuthStore = new CalendarAuthStore();
